import { useEffect } from "react";

interface PageMetaProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  schemaObjects?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  schemaObjects = [],
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    const canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (canonical) {
      canonical.setAttribute("href", canonicalUrl);
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalUrl);
      document.head.appendChild(link);
    }

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonicalUrl, true);

    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    const scriptId = "schema-ld-json";
    const existingScripts = document.querySelectorAll(`#${scriptId}`);
    existingScripts.forEach((s) => s.remove());

    schemaObjects.forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll(`#${scriptId}`);
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, schemaObjects]);

  return null;
}